import { useState, useEffect, useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Home,
  Sparkles,
  Wand2,
  Brain,
  ShieldCheck,
  ClipboardList,
  Activity,
  Search,
  FolderTree,
  Network,
  Boxes,
  Database,
  Landmark,
  Coins,
  Cloud,
  Cable,
  Gauge,
  ClipboardCheck,
  Milestone,
  Cpu,
  MonitorPlay,
  FileSignature,
} from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import type { NavItemConfig } from '@/components/layout/SidebarNavItem'
import { CommandPalette } from '@/components/CommandPalette'
import { ChatPanel } from '@/components/ChatPanel'
import { PanelConexion } from '@/components/PanelConexion'
import { useChatStore } from '@/stores/chat'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { logAarpia } from '@/lib/log'
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts'
import { Button } from '@/components/ui/button'

/**
 * DashboardLayout — sidebar DERIVADO-PURO (ORDEN-B, cierre AC5).
 *
 * Tres capas, ninguna con lista de entidades hardcodeada:
 *
 *  1. CHROME (conector-universal, fijo, siempre visible): Inicio, catálogo, mis
 *     fichas, gobierno, apariencia, marketplace. No son entidades — son las
 *     funciones app-level que TODO cliente necesita.
 *
 *  2. OPERAR (DERIVADO): `GET /api/v1/canonico?app_id=<app activo>` ya agrupa por
 *     subdominio las entidades del app filtradas `modulos_activos ∩ §32 ∩ RBAC`
 *     (el backend computa la intersección — su capa §32 filtra por destinatario,
 *     idéntico al enforce, cero divergencia). El front SOLO consume el resultado:
 *     una sección por subdominio (`sección = capitalize(subdominio)`), un ítem por
 *     entidad → `/app/operar/<sub>/<token>/<estado>`. Sin `gateEntidad`, sin
 *     `/mis-suscripciones`. (Dependencia futura F5/ADR-001: cuando se siembre
 *     `subdominio.registrado`, la sección leerá su alias mutable — cambio aditivo.)
 *
 *  3. CAPACIDADES de fábrica (bespoke, UX propia del Arquitecto = su especialización
 *     declarada): Crear con IA, alta cliente, publicar, monitorización, memoria.
 *     Conservan su ruta bespoke; su visibilidad se DERIVA de la presencia de su
 *     entidad en el mismo resultado de `?app_id` (sustituye el gating §32 que antes
 *     salía de `/mis-suscripciones`). Sus entidades se EXCLUYEN de la capa 2 para no
 *     duplicar (aparecen como wizard, no como tabla genérica).
 */

// Icono por SUBDOMINIO (no per-entity; ~pocas entradas). Default para subdominios nuevos.
const SUB_ICONS: Record<string, LucideIcon> = {
  infra: Boxes,
  registros: Coins,
  integracion: Network,
  maestros: FolderTree,
  ia: Brain,
  auth: ShieldCheck,
  sistema: Cloud,
  gobernanza: Landmark,
  claves: Database,
  construccion: Wand2,
}
const SUB_ICON_DEFAULT: LucideIcon = Boxes

// Capa 1 — CHROME fijo (ASR-8, decisión operador 2026-06-07): Inicio → Visor de
// apps (catálogo propio del Arquitecto: apps + interfaces, server-derived) →
// Monitorización (desplegable) → grupos derivados → usuario abajo.
// Capacidades redundantes y acciones legacy ELIMINADAS del sidebar: el catálogo
// ES entidad.disponible (grupos), gobierno = grupos Auth/Gobernanza, Marketplace
// = grupo derivado homónimo; alta/generador viven en las cards del Home;
// Apariencia vive en el desplegable del usuario; deploy-cliente retirado (B2.3).
const CHROME_NAV: NavItemConfig[] = [
  { icon: Home, label: 'Inicio', path: '/app', end: true },
  { icon: MonitorPlay, label: 'Visor de apps', path: '/app/visor' },
  // VC-3 (1da5c7a4, ratificado top-level): Módulos es la superficie de consumo del
  // cliente real, no telemetría — bajo Monitorización solo la encontraría un operador.
  { icon: Boxes, label: 'Módulos', path: '/app/modulos' },
  // VC-1: sin esta entrada la página Contratos solo se alcanza por Ctrl+K — la
  // verificación de la orden exige encontrar "lo contratado" en ≤2 clics.
  { icon: FileSignature, label: 'Contratos', path: '/app/suscripciones' },
  {
    icon: Activity,
    label: 'Monitorización',
    path: '/app/monitorizacion/resumen',
    children: [
      { icon: Activity, label: 'Resumen 360°', path: '/app/monitorizacion/resumen' },
      { icon: Cable, label: 'Conexiones', path: '/app/monitorizacion/conexiones' },
      { icon: ClipboardList, label: 'Fichas (agregado)', path: '/app/monitorizacion/fichas' },
      { icon: Gauge, label: 'Indicadores', path: '/app/monitorizacion/indicadores' },
      { icon: ClipboardCheck, label: 'Órdenes', path: '/app/monitorizacion/ordenes' },
      { icon: Milestone, label: 'Plan', path: '/app/monitorizacion/plan' },
      { icon: Cpu, label: 'Motor', path: '/app/monitorizacion/motor' },
      { icon: Network, label: 'Fractalidad', path: '/app/monitorizacion/fractalidad' },
    ],
  },
]

// Capa 3 — sin capacidades fijas (ASR-8); se conserva el mecanismo de gating por
// si una capacidad futura lo necesita (cero hardcode: gateEntidad sigue derivado).
const CAPACIDADES: NavItemConfig[] = []

const ENTIDADES_BESPOKE = new Set(
  CAPACIDADES.map((c) => c.gateEntidad).filter((x): x is string => !!x),
)

interface EntidadItem {
  nombre: string
  estado_deseable: string
  version: string
  status?: string
}
interface CanonicoResp {
  data?: Record<string, EntidadItem[]>
}

/** "entidad-de-negocio" → "Entidad de negocio". */
function legible(token: string): string {
  const s = (token || '').replace(/-/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}
/** "integracion" → "Integracion" (sección = subdominio capitalizado). */
function seccion(sub: string): string {
  return sub.charAt(0).toUpperCase() + sub.slice(1)
}
function rutaOperar(sub: string, nombre: string, estado: string): string {
  const token = nombre.split('.')[2] || ''
  return `/app/operar/${sub}/${token}/${estado}`
}

export function DashboardLayout() {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const toggleChat = useChatStore((s) => s.toggle)
  const token = useAuthStore((s) => s.token)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  useGlobalShortcuts({ onOpenPalette: () => setPaletteOpen(true), onToggleChat: toggleChat })

  // FUENTE ÚNICA del sidebar (ASR-2, orden a744caef): el Arquitecto es LA HERRAMIENTA
  // del operador, no una app-cliente — su catálogo NO se filtra por modulos_activos.
  // Derivación server-side: GET /canonico = catálogo ∩ permisos(usuario×CO) ∩ §32,
  // agrupado por subdominio; aquí solo se EXCLUYEN las deprecadas usando el status
  // que expone la API (cero hardcode; la deprecación gobernada las saca sola).
  const [porSub, setPorSub] = useState<Record<string, EntidadItem[]>>({})
  const [expuestas, setExpuestas] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!token) {
      setPorSub({})
      setExpuestas(new Set())
      return
    }
    let cancel = false
    api
      .get<CanonicoResp>(`/canonico`, token)
      .then((r) => {
        if (cancel) return
        const cruda = r.data || {}
        // − deprecadas (status del server; 'archived' no llega — sin URL §33)
        const data: typeof cruda = {}
        for (const [sub, arr] of Object.entries(cruda)) {
          const vivas = (arr || []).filter((e: any) => e.status !== 'deprecated')
          if (vivas.length) data[sub] = vivas
        }
        setPorSub(data)
        const set = new Set<string>()
        for (const arr of Object.values(data)) for (const e of arr) set.add(e.nombre)
        setExpuestas(set)
      })
      .catch((e) => {
        // NO degradar a vacío ante un hipo de red: esconder todo es peor que
        // mantener el último catálogo bueno. Solo se limpia al cerrar sesión.
        if (!cancel) logAarpia.warn('No se pudo derivar el sidebar de /canonico; se mantiene el previo:', e?.message || e)
      })
    return () => {
      cancel = true
    }
  }, [token, activeCoId])

  // Capa 2 — grupos operativos DERIVADOS, uno por subdominio (excluyendo las
  // entidades-bespoke). Subdominios ordenados; entidades en el orden del backend.
  const gruposOperativos = useMemo<NavItemConfig[]>(() => {
    return Object.keys(porSub)
      .sort()
      .map((sub) => {
        const icon = SUB_ICONS[sub] ?? SUB_ICON_DEFAULT
        const ents = (porSub[sub] || []).filter((e) => !ENTIDADES_BESPOKE.has(e.nombre))
        if (ents.length === 0) return null
        // Desambiguación: varias entidades comparten token (app.configurada /
        // app.generada / app.desplegada). Si el token se repite en el subdominio,
        // el label añade el estado deseable ("App · configurada") para no colisionar.
        const tokenCount: Record<string, number> = {}
        for (const e of ents) {
          const t = e.nombre.split('.')[2] || ''
          tokenCount[t] = (tokenCount[t] || 0) + 1
        }
        return {
          icon,
          label: seccion(sub),
          path: rutaOperar(sub, ents[0].nombre, ents[0].estado_deseable),
          children: ents.map((e) => {
            const t = e.nombre.split('.')[2] || ''
            const label = tokenCount[t] > 1 ? `${legible(t)} · ${e.estado_deseable}` : legible(t)
            return { icon, label, path: rutaOperar(sub, e.nombre, e.estado_deseable) }
          }),
        } as NavItemConfig
      })
      .filter((g): g is NavItemConfig => g !== null)
  }, [porSub])

  // Capa 3 — capacidades visibles si su entidad está expuesta en el catálogo §32.
  const capacidadesVisibles = useMemo<NavItemConfig[]>(
    () => CAPACIDADES.filter((c) => !c.gateEntidad || expuestas.has(c.gateEntidad)),
    [expuestas],
  )

  const navItems = useMemo<NavItemConfig[]>(
    () => [...CHROME_NAV, ...gruposOperativos, ...capacidadesVisibles],
    [gruposOperativos, capacidadesVisibles],
  )

  return (
    <AppShell navItems={navItems}>
      {/* Toolbar superior (buscar + asistente) — escritorio. En móvil se oculta para no
          robar espacio al contenido/chat (mobile-first diáfano); el menú va en la
          mini-topbar del shell y el asistente es la propia superficie del cliente. */}
      <div className="mb-6 hidden items-center justify-end gap-2 sm:flex">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPaletteOpen(true)}
          className="gap-2 text-muted-foreground"
          title="Buscar (Ctrl+K)"
          aria-label="Buscar"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Buscar</span>
          <kbd className="ml-1 hidden md:inline-flex pointer-events-none items-center rounded border border-border/60 bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
            ⌘K
          </kbd>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleChat}
          className="gap-2 text-muted-foreground"
          title="Asistente (Ctrl+J)"
          aria-label="Abrir asistente"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">Asistente</span>
        </Button>
      </div>

      <Outlet />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ChatPanel />
      <PanelConexion />
    </AppShell>
  )
}
